import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

import type {
  TaskLink,
  TaskDetail,
  TaskStatus,
  TaskSummary,
  TaskComment,
  TaskActivity,
  ChecklistItem,
  TaskListParams,
  TaskAttachment,
  MoveTaskPayload,
  BulkTasksPayload,
  TaskListResponse,
  TaskBoardResponse,
  CreateTaskPayload,
  UpdateTaskPayload,
  CreateStatusPayload,
  UpdateStatusPayload,
  DeleteStatusPayload,
  TaskCalendarResponse,
} from '../types/task.types';

export const tasksApi = createApi({
  reducerPath: 'tasksApi',
  baseQuery,
  tagTypes: ['Tasks', 'TaskDetail', 'TaskStatuses', 'TaskSummary', 'TaskComments', 'TaskActivity'],
  endpoints: (builder) => ({
    // Statuses
    getTaskStatuses: builder.query<TaskStatus[], void>({
      query: () => ({
        url: 'api/tasks/statuses',
        method: 'GET',
      }),
      transformResponse: (res: { data?: TaskStatus[] } | TaskStatus[]) =>
        Array.isArray(res) ? res : res?.data ?? [],
      providesTags: ['TaskStatuses'],
    }),

    createTaskStatus: builder.mutation<TaskStatus, CreateStatusPayload>({
      query: (body) => ({
        url: 'api/tasks/statuses',
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: TaskStatus }) => res?.data ?? ({} as TaskStatus),
      invalidatesTags: ['TaskStatuses', 'Tasks'],
    }),

    updateTaskStatus: builder.mutation<TaskStatus, { statusId: string; body: UpdateStatusPayload }>({
      query: ({ statusId, body }) => ({
        url: `api/tasks/statuses/${statusId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (res: { data?: TaskStatus }) => res?.data ?? ({} as TaskStatus),
      invalidatesTags: ['TaskStatuses', 'Tasks'],
    }),

    deleteTaskStatus: builder.mutation<{ moved: number }, { statusId: string; body?: DeleteStatusPayload }>({
      query: ({ statusId, body }) => ({
        url: `api/tasks/statuses/${statusId}`,
        method: 'DELETE',
        body,
      }),
      transformResponse: (res: { data?: { moved: number } }) => res?.data ?? { moved: 0 },
      invalidatesTags: ['TaskStatuses', 'Tasks'],
    }),

    // Summary counters
    getTaskSummary: builder.query<
      TaskSummary,
      { timezone?: string; includeSubtasks?: boolean } | void
    >({
      query: (params) => {
        const tz = params?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const subtasks = params?.includeSubtasks ? '&includeSubtasks=true' : '';
        return {
          url: `api/tasks/summary?timezone=${encodeURIComponent(tz)}${subtasks}`,
          method: 'GET',
        };
      },
      transformResponse: (res: { data?: TaskSummary }) =>
        res?.data ?? {
          assigned: 0,
          overdue: 0,
          dueToday: 0,
          upcoming: 0,
          completedThisWeek: 0,
          watching: 0,
          all: 0,
          unassigned: 0,
          noDueDate: 0,
        },
      providesTags: ['TaskSummary'],
    }),

    // List view
    // Active users that tasks can be assigned to
    getAssignableUsers: builder.query<Array<{ id: string; name: string }>, void>({
      query: () => ({
        url: 'api/users/list',
        method: 'POST',
        body: {
          filters: [
            { fieldname: 'status', search: 'ACTIVE' },
            { fieldname: 'firstName', sort: 1 },
          ],
          globalSearch: '',
          page: 1,
          limit: 200,
        },
      }),
      transformResponse: (res: { data?: { list?: any[] } | any[] }) => {
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.list ?? []);
        return list.map((u: any) => ({
          id: u.id ?? u._id ?? '',
          name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id,
        }));
      },
    }),

    getTaskList: builder.query<TaskListResponse, TaskListParams>({
      query: (body) => ({
        url: 'api/tasks/list',
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: TaskListResponse } | TaskListResponse) => {
        if ('data' in res && res.data) return res.data;
        return res as TaskListResponse;
      },
      providesTags: ['Tasks'],
    }),

    // Board view
    getTaskBoard: builder.query<TaskBoardResponse, TaskListParams>({
      query: (body) => ({
        url: 'api/tasks/board',
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: TaskBoardResponse } | TaskBoardResponse) => {
        if ('data' in res && res.data) return res.data;
        return res as TaskBoardResponse;
      },
      providesTags: ['Tasks'],
    }),

    // Calendar view
    getTaskCalendar: builder.query<TaskCalendarResponse, { from: string; to: string } & TaskListParams>({
      query: (body) => ({
        url: 'api/tasks/calendar',
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: TaskCalendarResponse } | TaskCalendarResponse) => {
        if ('data' in res && res.data) return res.data;
        return res as TaskCalendarResponse;
      },
      providesTags: ['Tasks'],
    }),

    // Create task
    createTask: builder.mutation<TaskDetail, CreateTaskPayload>({
      query: (body) => ({
        url: 'api/tasks',
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: TaskDetail }) => res?.data ?? ({} as TaskDetail),
      invalidatesTags: ['Tasks', 'TaskSummary'],
    }),

    // Bulk actions
    bulkTasks: builder.mutation<{ updated: number; failed: any[] }, BulkTasksPayload>({
      query: (body) => ({
        url: 'api/tasks/bulk',
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: { updated: number; failed: any[] } }) =>
        res?.data ?? { updated: 0, failed: [] },
      invalidatesTags: ['Tasks', 'TaskSummary'],
    }),

    // Delete tasks
    deleteTasks: builder.mutation<{ deleted: number }, { ids: string[] }>({
      query: (body) => ({
        url: 'api/tasks',
        method: 'DELETE',
        body,
      }),
      transformResponse: (res: { data?: { deleted: number } }) => res?.data ?? { deleted: 0 },
      invalidatesTags: ['Tasks', 'TaskSummary'],
    }),

    // Get detail
    getTaskDetail: builder.query<TaskDetail, string>({
      query: (id) => ({
        url: `api/tasks/${id}`,
        method: 'GET',
      }),
      transformResponse: (res: { data?: TaskDetail }) => res?.data ?? ({} as TaskDetail),
      providesTags: (_result, _error, id) => [{ type: 'TaskDetail', id }],
    }),

    // Update task
    updateTask: builder.mutation<TaskDetail, { id: string; body: UpdateTaskPayload }>({
      query: ({ id, body }) => ({
        url: `api/tasks/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (res: { data?: TaskDetail }) => res?.data ?? ({} as TaskDetail),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'TaskDetail', id },
        'Tasks',
        'TaskSummary',
      ],
    }),

    // Drag & drop move task
    moveTask: builder.mutation<TaskDetail, { id: string; body: MoveTaskPayload }>({
      query: ({ id, body }) => ({
        url: `api/tasks/${id}/move`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: TaskDetail }) => res?.data ?? ({} as TaskDetail),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'TaskDetail', id },
        'Tasks',
        'TaskSummary',
      ],
    }),

    // Subtasks
    createSubtask: builder.mutation<TaskDetail, { parentId: string; body: CreateTaskPayload }>({
      query: ({ parentId, body }) => ({
        url: `api/tasks/${parentId}/subtasks`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: TaskDetail }) => res?.data ?? ({} as TaskDetail),
      invalidatesTags: (_result, _error, { parentId }) => [
        { type: 'TaskDetail', id: parentId },
        'Tasks',
        'TaskSummary',
      ],
    }),

    // Activity
    getTaskActivity: builder.query<TaskActivity[], { id: string; limit?: number }>({
      query: ({ id, limit = 100 }) => ({
        url: `api/tasks/${id}/activity?limit=${limit}`,
        method: 'GET',
      }),
      transformResponse: (res: { data?: TaskActivity[] } | TaskActivity[]) =>
        Array.isArray(res) ? res : res?.data ?? [],
      providesTags: (_result, _error, { id }) => [{ type: 'TaskActivity', id }],
    }),

    // Checklist
    addChecklistItem: builder.mutation<ChecklistItem, { taskId: string; title: string }>({
      query: ({ taskId, title }) => ({
        url: `api/tasks/${taskId}/checklist`,
        method: 'POST',
        body: { title },
      }),
      transformResponse: (res: { data?: ChecklistItem }) => res?.data ?? ({} as ChecklistItem),
      invalidatesTags: (_result, _error, { taskId }) => [{ type: 'TaskDetail', id: taskId }, 'Tasks'],
    }),

    updateChecklistItem: builder.mutation<
      ChecklistItem,
      { taskId: string; itemId: string; body: { title?: string; isDone?: boolean; position?: number } }
    >({
      query: ({ taskId, itemId, body }) => ({
        url: `api/tasks/${taskId}/checklist/${itemId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (res: { data?: ChecklistItem }) => res?.data ?? ({} as ChecklistItem),
      invalidatesTags: (_result, _error, { taskId }) => [{ type: 'TaskDetail', id: taskId }, 'Tasks'],
    }),

    deleteChecklistItem: builder.mutation<void, { taskId: string; itemId: string }>({
      query: ({ taskId, itemId }) => ({
        url: `api/tasks/${taskId}/checklist/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { taskId }) => [{ type: 'TaskDetail', id: taskId }, 'Tasks'],
    }),

    // Comments
    getTaskComments: builder.query<
      { list: TaskComment[]; total: number; page: number; limit: number },
      { id: string; page?: number; limit?: number }
    >({
      query: ({ id, page = 1, limit = 50 }) => ({
        url: `api/tasks/${id}/comments?page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      transformResponse: (res: { data?: { list: TaskComment[]; total: number; page: number; limit: number } }) =>
        res?.data ?? { list: [], total: 0, page: 1, limit: 50 },
      providesTags: (_result, _error, { id }) => [{ type: 'TaskComments', id }],
    }),

    addComment: builder.mutation<
      TaskComment,
      { taskId: string; body: { content: string; mentions?: string[]; attachments?: any[] } }
    >({
      query: ({ taskId, body }) => ({
        url: `api/tasks/${taskId}/comments`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: TaskComment }) => res?.data ?? ({} as TaskComment),
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskComments', id: taskId },
        { type: 'TaskDetail', id: taskId },
        { type: 'TaskActivity', id: taskId },
        'Tasks',
      ],
    }),

    updateComment: builder.mutation<TaskComment, { taskId: string; commentId: string; content: string }>({
      query: ({ taskId, commentId, content }) => ({
        url: `api/tasks/${taskId}/comments/${commentId}`,
        method: 'PATCH',
        body: { content },
      }),
      transformResponse: (res: { data?: TaskComment }) => res?.data ?? ({} as TaskComment),
      invalidatesTags: (_result, _error, { taskId }) => [{ type: 'TaskComments', id: taskId }],
    }),

    deleteComment: builder.mutation<void, { taskId: string; commentId: string }>({
      query: ({ taskId, commentId }) => ({
        url: `api/tasks/${taskId}/comments/${commentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskComments', id: taskId },
        { type: 'TaskDetail', id: taskId },
        'Tasks',
      ],
    }),

    // Attachments
    addAttachment: builder.mutation<
      TaskAttachment,
      {
        taskId: string;
        body: { fileUrl: string; filename: string; mimeType: string; fileSize: number; fileType?: string };
      }
    >({
      query: ({ taskId, body }) => ({
        url: `api/tasks/${taskId}/attachments`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: TaskAttachment }) => res?.data ?? ({} as TaskAttachment),
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskDetail', id: taskId },
        { type: 'TaskActivity', id: taskId },
        'Tasks',
      ],
    }),

    deleteAttachment: builder.mutation<void, { taskId: string; attachmentId: string }>({
      query: ({ taskId, attachmentId }) => ({
        url: `api/tasks/${taskId}/attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskDetail', id: taskId },
        { type: 'TaskActivity', id: taskId },
        'Tasks',
      ],
    }),

    // Watch / Unwatch
    toggleWatchTask: builder.mutation<{ watching: boolean }, { taskId: string; watch: boolean }>({
      query: ({ taskId, watch }) => ({
        url: `api/tasks/${taskId}/watch`,
        method: watch ? 'POST' : 'DELETE',
      }),
      transformResponse: (res: { data?: { watching: boolean } }) => res?.data ?? { watching: false },
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskDetail', id: taskId },
        'TaskSummary',
      ],
    }),

    // Record Links
    addTaskLink: builder.mutation<TaskLink[], { taskId: string; body: { entityType: string; entityId: string } }>({
      query: ({ taskId, body }) => ({
        url: `api/tasks/${taskId}/links`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: { data?: TaskLink[] }) => res?.data ?? [],
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskDetail', id: taskId },
        { type: 'TaskActivity', id: taskId },
        'Tasks',
      ],
    }),

    deleteTaskLink: builder.mutation<TaskLink[], { taskId: string; body: { entityType: string; entityId: string } }>({
      query: ({ taskId, body }) => ({
        url: `api/tasks/${taskId}/links`,
        method: 'DELETE',
        body,
      }),
      transformResponse: (res: { data?: TaskLink[] }) => res?.data ?? [],
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'TaskDetail', id: taskId },
        { type: 'TaskActivity', id: taskId },
        'Tasks',
      ],
    }),
  }),
});

export const {
  useGetTaskStatusesQuery,
  useCreateTaskStatusMutation,
  useUpdateTaskStatusMutation,
  useDeleteTaskStatusMutation,
  useGetTaskSummaryQuery,
  useGetAssignableUsersQuery,
  useGetTaskListQuery,
  useLazyGetTaskListQuery,
  useGetTaskBoardQuery,
  useGetTaskCalendarQuery,
  useCreateTaskMutation,
  useBulkTasksMutation,
  useDeleteTasksMutation,
  useGetTaskDetailQuery,
  useUpdateTaskMutation,
  useMoveTaskMutation,
  useCreateSubtaskMutation,
  useGetTaskActivityQuery,
  useAddChecklistItemMutation,
  useUpdateChecklistItemMutation,
  useDeleteChecklistItemMutation,
  useGetTaskCommentsQuery,
  useAddCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useAddAttachmentMutation,
  useDeleteAttachmentMutation,
  useToggleWatchTaskMutation,
  useAddTaskLinkMutation,
  useDeleteTaskLinkMutation,
} = tasksApi;
